import { useEffect, useMemo, useRef, useState } from 'react'

const pad2 = value => String(value).padStart(2, '0')

const toLocalDateISO = date =>
	`${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const toTimeString = date => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`

const getDefaultOpenData = () => {
	const now = new Date()
	return {
		openDate: toLocalDateISO(now),
		openTime: toTimeString(now),
	}
}

const parseDateTime = (dateISO, timeStr) => {
	if (!dateISO || !timeStr) return null
	const [hours, minutes] = timeStr.split(':').map(Number)
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

	const d = new Date(dateISO)
	if (Number.isNaN(d.getTime())) return null
	d.setHours(hours || 0, minutes || 0, 0, 0)
	return d
}

const formatFullDateTime = dt => {
	if (!dt) return '—'
	return dt.toLocaleString(undefined, {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

const formatDateOnly = dt => {
	if (!dt) return ''
	return dt.toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
	})
}

const formatCompactDateTime = dt => {
	if (!dt) return 'Not set'
	return dt.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	})
}

const getCalendarDayDiff = (start, end) => {
	if (!start || !end) return null
	const startDay = new Date(start)
	const endDay = new Date(end)
	startDay.setHours(0, 0, 0, 0)
	endDay.setHours(0, 0, 0, 0)
	return Math.round((endDay - startDay) / (1000 * 60 * 60 * 24))
}

const formatDuration = (start, end) => {
	if (!start || !end) return 'Duration: —'
	if (end <= start) return 'Duration: 0 days'
	const days = getCalendarDayDiff(start, end)
	if (!Number.isFinite(days) || days < 0) return 'Duration: 0 days'
	return `Duration: ${days} day${days === 1 ? '' : 's'}`
}

const formatDurationValue = (start, end) => {
	if (!start || !end) return 'Deadline not set'
	if (end <= start) return 'Invalid range'
	const days = getCalendarDayDiff(start, end)
	if (!Number.isFinite(days) || days < 0) return 'Invalid range'
	return `${days} day${days === 1 ? '' : 's'}`
}

export const useStepTimeline = (onValidityChange, initialData = {}) => {
	const [openDate, setOpenDate] = useState(
		() => initialData.openDate || getDefaultOpenData().openDate,
	)
	const [openTime, setOpenTime] = useState(
		() => initialData.openTime || getDefaultOpenData().openTime,
	)
	const [closeDate, setCloseDate] = useState(() => initialData.closeDate || '')
	const [closeTime, setCloseTime] = useState(() => initialData.closeTime || '')

	const stepKeyRef = useRef(null)
	useEffect(() => {
		const defaults = getDefaultOpenData()
		const openDateStr = initialData.openDate || defaults.openDate
		const openTimeStr = initialData.openTime || defaults.openTime
		const closeDateStr = initialData.closeDate || ''
		const closeTimeStr = initialData.closeTime || ''

		const currentKey = `${openDateStr}|${openTimeStr}|${closeDateStr}|${closeTimeStr}`
		if (currentKey !== stepKeyRef.current) {
			setOpenDate(openDateStr)
			setOpenTime(openTimeStr)
			setCloseDate(closeDateStr)
			setCloseTime(closeTimeStr)
			stepKeyRef.current = currentKey
		}
	}, [
		initialData.openDate,
		initialData.openTime,
		initialData.closeDate,
		initialData.closeTime,
	])

	const openDt = useMemo(
		() => parseDateTime(openDate, openTime),
		[openDate, openTime],
	)
	const closeDt = useMemo(
		() => parseDateTime(closeDate, closeTime),
		[closeDate, closeTime],
	)

	const closePresetOptions = useMemo(() => {
		const base = openDt || parseDateTime(openDate, openTime) || new Date()
		return [7, 10, 15].map(days => {
			const dt = new Date(base)
			dt.setDate(dt.getDate() + days)
			return {
				days,
				label: `${days} days`,
				preview: formatDateOnly(dt),
			}
		})
	}, [openDate, openDt, openTime])

	const activePresetDays = useMemo(() => {
		if (!openDt || !closeDt) return null
		const currentDayDiff = getCalendarDayDiff(openDt, closeDt)
		return closePresetOptions.find(option => option.days === currentDayDiff)?.days || null
	}, [closeDt, closePresetOptions, openDt])

	const durationText = useMemo(
		() => formatDuration(openDt, closeDt),
		[openDt, closeDt],
	)

	const durationValueText = useMemo(
		() => formatDurationValue(openDt, closeDt),
		[openDt, closeDt],
	)

	const openSummaryText = useMemo(
		() => formatCompactDateTime(openDt),
		[openDt],
	)

	const closeSummaryText = useMemo(
		() => formatCompactDateTime(closeDt),
		[closeDt],
	)

	const isValidRange = useMemo(
		() => !!openDt && !!closeDt && closeDt > openDt,
		[openDt, closeDt],
	)

	const handleCloseDateChange = value => {
		setCloseDate(value)
		if (value && !closeTime) {
			setCloseTime(openTime || '23:59')
		}
	}

	const applyClosePreset = days => {
		const base = openDt || parseDateTime(openDate, openTime) || new Date()
		const close = new Date(base)
		close.setDate(close.getDate() + days)
		setCloseDate(toLocalDateISO(close))
		setCloseTime(toTimeString(close))
	}

	useEffect(() => {
		if (typeof onValidityChange === 'function') {
			onValidityChange(isValidRange)
		}
	}, [isValidRange, onValidityChange])

	return {
		openDate,
		openTime,
		closeDate,
		closeTime,
		setOpenDate,
		setOpenTime,
		setCloseDate: handleCloseDateChange,
		setCloseTime,
		openDt,
		closeDt,
		closePresetOptions,
		activePresetDays,
		applyClosePreset,
		durationText,
		durationValueText,
		openSummaryText,
		closeSummaryText,
		formatFullDateTime,
		isValidRange,
	}
}
